'use client';

import React, { useEffect, useRef, useState } from 'react';

interface ServiceCard {
  tag: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  stagger: number;
}

const services: ServiceCard[] = [
  {
    tag: 'Kernleistung',
    title: 'Finanzbuchhaltung',
    description: 'Laufende Buchführung, Kontierung und Abstimmung — ordnungsgemäß, termingerecht und vollständig nach HGB.',
    stagger: 1,
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    tag: 'Personal',
    title: 'Lohnbuchhaltung',
    description: 'Monatliche Lohn- und Körperabrechnungen, Meldewesen, Sozialversicherung und Körpersteueranmeldungen.',
    stagger: 2,
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    tag: 'Abschluss',
    title: 'Jahresabschlüsse',
    description: 'Vorbereitende Tätigkeiten für Jahresabschlüsse',
    stagger: 3,
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    tag: 'Steuern',
    title: 'Einkommensteuererklärung',
    description: 'Vorbereitende Tätigkeiten für Einkommensteuererklärungen – Zusammenstellung und Aufbereitung der erforderlichen Unterlagen für die steuerliche Bearbeitung.',
    stagger: 4,
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  {
    tag: 'Service',
    title: 'Büroservice & Organisation',
    description: 'Administrative Unterstützung, Belegorganisation, Postbearbeitung und strukturierte Ablage — Ihr Büro in besten Händen.',
    stagger: 5,
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  {
    tag: 'Digital',
    title: 'Lexoffice-Unterstützung',
    description: 'Einrichtung, Schulung und laufende Unterstützung bei der Buchhaltungssoftware Lexoffice — einfach und digital.',
    stagger: 6,
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.scroll-reveal-init');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="leistungen" className="py-16 md:py-32 px-5 md:px-10" style={{ backgroundColor: 'rgba(232,228,223,0.4)' }} ref={sectionRef}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12 md:mb-20 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-7 scroll-reveal-init">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] mb-5" style={{ color: 'var(--accent)' }}>
              <span className="gold-dot" />
              Unsere Leistungen
            </span>
            <h2 className="font-display text-hero-sm font-semibold text-balance" style={{ color: 'var(--foreground)' }}>
              Alles aus einer{' '}
              <span className="italic">verlässlichen</span>{' '}Hand
            </h2>
          </div>
          <div className="md:col-span-5 scroll-reveal-init stagger-2">
            <p className="text-base md:text-lg leading-relaxed font-light" style={{ color: 'var(--muted-foreground)' }}>
              Von der laufenden Buchführung bis zum Jahresabschluss — wir übernehmen die Zahlenarbeit, damit Sie sich auf Ihr Kerngeschäft konzentrieren können.
            </p>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {services.map((service) => (
            <ServiceCard key={service.title} service={service} />
          ))}
        </div>

        {/* Legal Notice */}
        <p className="mt-10 text-xs font-light text-center" style={{ color: 'var(--muted-foreground)' }}>
          Unsere Buchhaltungsleistungen erfolgen ausschließlich im gesetzlich zulässigen Umfang gemäß § 6 Nr. 3 und 4 StBerG.
        </p>
      </div>
    </section>
  );
}

function ServiceCard({ service }: { service: ServiceCard }) {
  const [hovered, setHovered] = React.useState(false);

  const handleLearnMore = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div
      className={`scroll-reveal-init stagger-${service.stagger} service-card-border group relative rounded-2xl p-6 md:p-8 border transition-all duration-400 cursor-default`}
      style={{
        backgroundColor: 'var(--card)',
        borderColor: hovered ? 'rgba(200,150,90,0.4)' : 'var(--border)',
        boxShadow: hovered ? '0 10px 15px -3px rgba(0,0,0,0.05)' : 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Tag */}
      <div className="absolute top-5 right-5 md:top-6 md:right-6">
        <span
          className="text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full"
          style={{ color: 'rgba(154,149,144,0.6)', backgroundColor: 'var(--secondary)' }}
        >
          {service.tag}
        </span>
      </div>

      {/* Icon */}
      <div
        className="w-11 h-11 md:w-12 md:h-12 rounded-xl flex items-center justify-center mb-5 transition-all duration-300"
        style={{
          backgroundColor: hovered ? 'var(--accent)' : 'var(--secondary)',
          color: hovered ? 'white' : 'var(--foreground)',
        }}
      >
        {service.icon}
      </div>

      {/* Title */}
      <h3
        className="font-display text-xl font-semibold mb-3 transition-colors duration-300"
        style={{ color: hovered ? 'var(--accent)' : 'var(--foreground)' }}
      >
        {service.title}
      </h3>

      {/* Description */}
      <p className="text-sm leading-relaxed font-light" style={{ color: 'var(--muted-foreground)' }}>
        {service.description}
      </p>

      {/* Link */}
      <a
        href="#kontakt"
        onClick={handleLearnMore}
        className="mt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-300 py-1 min-h-[44px]"
        style={{ color: hovered ? 'var(--accent)' : 'rgba(154,149,144,0.5)' }}
      >
        <span>Mehr erfahren</span>
        <svg
          className={`w-3 h-3 transition-transform ${hovered ? 'translate-x-1' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
      </a>
    </div>
  );
}
